import { InfoCircledIcon, PlusIcon } from "@radix-ui/react-icons";

import { AddImpressionsDialog } from "@/components/AddImpressionsDialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { CampaignStatus, Feed } from "@/lib/types";
import { formatBigNumber } from "@/lib/utils";

export function Impressions({ status }: { status: CampaignStatus }) {
  const current = status.impressions_to_date || 0;

  return (
    <>
      {current}
      <span className="text-slate-dim">
        {" / "}
        {status.impressions_budget + current}
      </span>
    </>
  );
}

export function Earned({ status }: { status: CampaignStatus }) {
  const earnedDollars = status.cpm_inflight / 100;
  return `$${earnedDollars.toFixed(2)}`;
}

export function EarnedTooltip({
  status,
  children,
}: {
  status: CampaignStatus;
  children: React.ReactNode;
}) {
  const earnedDollars = status.cpm_inflight / 100;
  const afterFees = earnedDollars - earnedDollars * 0.3;

  return (
    <SimpleTooltip message={`$${afterFees.toFixed(2)} payout`}>
      <div className="flex gap-0.5 items-center w-max">
        {children}
        <span className="sr-only">total. {afterFees.toFixed(2)} payout.</span>
        <InfoCircledIcon className="ml-2 h-3 w-3" />
      </div>
    </SimpleTooltip>
  );
}

export function TotalBudget({ status }: { status: CampaignStatus }) {
  return (
    <>
      $
      {(
        ((status.impressions_budget + (status.impressions_to_date ?? 0)) *
          (status.cpm_cents / 100)) /
        1000
      ).toFixed(2)}
    </>
  );
}

export function Cpm({ status }: { status: Pick<Feed, "cpm_cents"> }) {
  return <span>${((status.cpm_cents || 0) / 100).toFixed(2)}</span>;
}

export function CampaignAlgoAnalyticsBlock({
  campaignId,
  algo,
  isOwner,
}: {
  campaignId: number;
  algo: Feed & {
    details: CampaignStatus;
  };
  isOwner: boolean;
}) {
  const budget = (
    <div className="text-xl md:text-3xl flex gap-2 font-semibold">
      <Earned status={algo.details} />
      <span className="text-slate-dim">
        {" / "}
        <TotalBudget status={algo.details} />
      </span>
    </div>
  );

  return (
    <div className="flex flex-col md:flex-row justify-around gap-2 md:gap-4 w-full">
      <div className="flex flex-col gap-2 shadow-sm border border-slate-6 dark:border-slatedark-6 rounded-lg p-4 flex-1">
        <div className="text-xl md:text-3xl font-semibold">
          ${((algo?.cpm_cents || 0) / 100).toFixed(2)}
        </div>
        <div className="font-medium text-xs text-slate-dim">CPM</div>
      </div>
      <div className="flex flex-col gap-2 shadow-sm border border-slate-6 dark:border-slatedark-6 rounded-lg p-4 flex-1">
        <div className="flex items-center justify-between">
          <div className="text-xl md:text-3xl font-semibold">
            {algo?.details.impressions_to_date}
            <span className="text-slate-dim">
              {" / "}
              {formatBigNumber(
                algo?.details.impressions_budget +
                  algo?.details.impressions_to_date,
              )}
            </span>
          </div>
          {isOwner && (
            <AddImpressionsDialog
              campaignId={campaignId}
              feedId={algo.id}
              rate={algo.cpm_cents ?? 0}
            >
              <SimpleTooltip message="Add Impressions">
                <Button variant="ghost" size="smIcon">
                  <span className="sr-only">Add Impressions</span>
                  <PlusIcon className="w-4 h-4" />
                </Button>
              </SimpleTooltip>
            </AddImpressionsDialog>
          )}
        </div>
        <Progress
          aria-label="Impressions"
          value={algo.details.impressions_to_date ?? 0}
          max={
            algo.details.impressions_budget +
            (algo.details.impressions_to_date ?? 0)
          }
        />
        <div className="font-medium text-xs text-slate-dim">Impressions</div>
      </div>
      <div className="flex flex-col gap-2 shadow-sm border border-slate-6 dark:border-slatedark-6 rounded-lg p-4 flex-1">
        {isOwner ? (
          budget
        ) : (
          <EarnedTooltip status={algo.details}>{budget}</EarnedTooltip>
        )}
        <Progress
          aria-label="Order"
          value={algo.details.impressions_to_date ?? 0}
          max={
            algo.details.impressions_budget +
            (algo.details.impressions_to_date ?? 0)
          }
        />
        <div className="font-medium text-xs text-slate-dim">Order</div>
      </div>
    </div>
  );
}
